import { msg } from "../locales/translate.ts";
import { GD } from "../GD";
import { getClientListPage, type ClientListPage } from "./clientListData";
import { getClientListRequestData, type ClientListRequest } from "./clientListRequest";

type Context = Pick<IAPIRequest, "expectedProjectId" | "signal">;

const getContext = async (context: Context): Promise<Context> => {
	const projectId = context.expectedProjectId ?? (await GD.REQ_CURRENT_PROJECT.request())?.id;
	if (!projectId) throw new Error(msg("selectAProjectToLoadClients"));
	return { ...context, expectedProjectId: projectId };
};

const requestPage = async (filters: ClientListRequest, context: Context): Promise<ClientListPage> => {
	context.signal?.throwIfAborted();
	const response = await GD.REQ_API.request({ method: "clients/list", data: getClientListRequestData(filters), ...context });
	context.signal?.throwIfAborted();
	if (response.error) throw response.error;
	const page = getClientListPage(response.data);
	if (!page) throw new Error(msg("unexpectedClientsListResponse"));
	return page;
};

export const loadClientListPage = async (filters: ClientListRequest, context: Context = {}) =>
	requestPage(filters, await getContext(context));

// Selectors and cross-client navigation need the complete list, not the first page.
export const loadAllClients = async (filters: ClientListRequest = {}, context: Context = {}): Promise<ClientListPage> => {
	const scope = await getContext(context);
	let current = await requestPage({ ...filters, page: 0, per_page: filters.per_page ?? 100 }, scope);
	if (current.page !== 0) throw new Error(msg("couldNotLoadTheFirstClientPagePleaseRetry"));
	const first = current;
	const items = new Map(current.items.map(client => [client.id, client]));
	while ((current.page + 1) * current.per_page < current.total) {
		const next = await requestPage({ ...filters, page: current.page + 1, per_page: current.per_page }, scope);
		if (next.page !== current.page + 1 || next.per_page !== current.per_page || next.items.length === 0) throw new Error(msg("couldNotLoadAllClientsPleaseRetry"));
		const previousSize = items.size;
		for (const client of next.items) items.set(client.id, client);
		if (items.size === previousSize) throw new Error(msg("clientListDidNotAdvancePleaseRetry"));
		if (next.truncated) throw new Error(msg("clientListIsIncompleteNarrowYourSearch"));
		current = next;
	}
	if (first.truncated || items.size < current.total) throw new Error(msg("clientListIsIncompleteNarrowYourSearch"));
	return { ...first, items: [...items.values()], total: items.size, page: 0, per_page: Math.max(1, items.size) };
};
